import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import toast from 'react-hot-toast';

/**
 * Inline Didit identity verification, in a modal, reusable across the app
 * (the Sell gate, Profile, anywhere a seller must verify). When it opens it
 * starts a Didit session and renders the steps in an iframe, so the user never
 * leaves Souqli. Didit returns the iframe to our same-origin callback
 * (.../dashboard/profile?idv=complete) when finished, which we detect on the
 * iframe's load event. A new-tab fallback covers phones where in-frame camera
 * access is fussy.
 */
export function IdVerifyModal({ open, onClose, onSubmitted }: {
  open: boolean;
  onClose: () => void;
  onSubmitted?: () => void;
}) {
  const qc = useQueryClient();
  const [url, setUrl] = useState<string | null>(null);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) { setUrl(null); setError(null); return; }
    let alive = true;
    setStarting(true);
    setError(null);
    api.post('/me/id-verification/start')
      .then(({ data }) => {
        if (!alive) return;
        if (data?.url) setUrl(data.url as string);
        else setError('Could not start verification. Please try again.');
      })
      .catch((e: any) => { if (alive) setError(e?.response?.data?.message ?? 'ID verification is unavailable right now.'); })
      .finally(() => { if (alive) setStarting(false); });
    return () => { alive = false; };
  }, [open]);

  function onFrameLoad(e: React.SyntheticEvent<HTMLIFrameElement>) {
    try {
      const href = e.currentTarget.contentWindow?.location?.href ?? '';
      if (href && (href.includes('idv=complete') || href.includes('/dashboard/profile'))) {
        qc.invalidateQueries({ queryKey: ['profile'] });
        qc.invalidateQueries({ queryKey: ['connect-status'] });
        toast.success('Thanks - we are confirming your identity now.');
        onSubmitted?.();
        onClose();
      }
    } catch { /* still on the verification partner's page */ }
  }

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 grid place-items-center p-4 bg-ink/60 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-[560px] overflow-hidden overflow-y-auto max-h-[92vh]" onClick={(ev) => ev.stopPropagation()}>
        <div className="px-6 py-4 border-b border-warmline flex items-center justify-between gap-3">
          <div>
            <div className="stamp text-[9px] text-gold-dark">VERIFY YOUR IDENTITY</div>
            <h3 className="display text-xl mt-0.5">A quick, secure check</h3>
          </div>
          <button onClick={onClose} className="text-mute hover:text-ink text-sm">Close</button>
        </div>
        <div className="p-4">
          <p className="text-[12px] text-mute mb-3 leading-relaxed">
            Have your passport or driving licence ready, and allow camera access when asked. The steps run right here, so you will not leave Souqli.
          </p>
          {error ? (
            <div className="py-10 text-center text-[13px] text-bad">{error}</div>
          ) : !url ? (
            <div className="py-16 text-center text-mute text-[13px]">{starting ? 'Starting your secure check…' : 'Loading…'}</div>
          ) : (
            <>
              <iframe
                src={url}
                title="Identity verification"
                allow="camera; microphone; fullscreen; autoplay; encrypted-media"
                className="w-full rounded-luxe border border-warmline bg-white"
                style={{ height: 'min(72vh, 620px)' }}
                onLoad={onFrameLoad}
              />
              <p className="text-[11px] text-mute mt-2">
                Trouble with the camera on your phone?{' '}
                <a href={url} target="_blank" rel="noopener noreferrer" className="text-gold-dark underline">Open the check in a new tab</a>.
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default IdVerifyModal;
